import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';

const brl = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const subtotal = (i) => (Number(i.quantidade) || 0) * (Number(i.valor_unitario) || 0) * (1 - (Number(i.desconto_percentual) || 0) / 100);

export function OrcamentoDialog({ 
  open, 
  onOpenChange, 
  form, 
  setForm, 
  onSubmit, 
  onReset,
  clientes = [],
  materiais = [],
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  editing = false
}) {
  const handleClose = () => {
    onOpenChange(false);
    onReset?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); else onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Orçamento' : 'Novo Orçamento'}</DialogTitle>
            <DialogDescription>{editing ? 'Ao salvar, o orçamento volta para rascunho para ser reenviado' : 'Crie um novo orçamento para um cliente'}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Cliente *</Label>
              <Select 
                value={form.cliente_id || undefined} 
                onValueChange={(v) => setForm(prev => ({ ...prev, cliente_id: v }))}
              >
                <SelectTrigger data-testid="select-cliente-orcamento">
                  <SelectValue placeholder="Selecione o cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Validade (dias)</Label>
                <Input 
                  type="number"
                  value={form.validade_dias}
                  onChange={(e) => setForm(prev => ({ ...prev, validade_dias: parseInt(e.target.value) || 30 }))}
                />
              </div>
              <div>
                <Label>Condições de Pagamento</Label>
                <Input 
                  value={form.condicoes_pagamento}
                  onChange={(e) => setForm(prev => ({ ...prev, condicoes_pagamento: e.target.value }))}
                  placeholder="Ex: 30/60/90 dias"
                />
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Itens do Orçamento</Label>
                <Button type="button" size="sm" variant="outline" onClick={onAddItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar Item
                </Button>
              </div>
              
              {form.itens.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Material</Label>
                    <Select 
                      value={item.material_id || undefined} 
                      onValueChange={(v) => onUpdateItem(index, 'material_id', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {materiais.map(m => (
                          <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-20">
                    <Label className="text-xs">Qtd</Label>
                    <Input 
                      type="number"
                      value={item.quantidade}
                      onChange={(e) => onUpdateItem(index, 'quantidade', parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <div className="w-28">
                    <Label className="text-xs">Valor Unit.</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      onFocus={(e) => e.target.select()}
                      value={item.valor_unitario}
                      onChange={(e) => onUpdateItem(index, 'valor_unitario', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="w-20">
                    <Label className="text-xs">Desc. %</Label>
                    <Input 
                      type="number"
                      value={item.desconto_percentual}
                      onChange={(e) => onUpdateItem(index, 'desconto_percentual', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="w-28 text-right">
                    <Label className="text-xs">Subtotal</Label>
                    <p className="h-10 flex items-center justify-end font-semibold text-slate-800 tabular-nums" data-testid={`orcamento-item-subtotal-${index}`}>
                      {brl(subtotal(item))}
                    </p>
                  </div>
                  <Button type="button" size="icon" variant="ghost" onClick={() => onRemoveItem(index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
              {form.itens.length > 0 && (
                <div className="flex justify-end items-center gap-3 mt-3 pt-3 border-t" data-testid="orcamento-total">
                  <span className="text-sm text-slate-600">{form.itens.reduce((s, i) => s + (Number(i.quantidade) || 0), 0)} item(ns) · Valor total</span>
                  <span className="text-xl font-bold text-slate-900 tabular-nums">{brl(form.itens.reduce((s, i) => s + subtotal(i), 0))}</span>
                </div>
              )}
            </div>
            
            <div>
              <Label>Observações</Label>
              <Textarea 
                value={form.observacoes}
                onChange={(e) => setForm(prev => ({ ...prev, observacoes: e.target.value }))}
                placeholder="Observações adicionais..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" data-testid="btn-salvar-orcamento">
              {editing ? 'Salvar Alterações' : 'Criar Orçamento'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
